import { ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Breadcrumbs, Button, Grid, IconButton, Typography, useTheme } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'

import { ImageInput } from './image-input.component'
import { IMenuItem, ItemsMenu, ScrollToButton } from 'src/components'

import { ArrowIcon } from 'src/assets/icons'
import { IBreadcrumb } from 'src/types'

import useStyles from './styles'
import { ColoredChip } from '../colored-chip'
import { ExternalLink } from 'src/assets/icons/v2/ExternalLink'
import { DEFAULT_LANGUAGE_CODE } from '../languages/langauges-list/constants'
import { createProductQRLink } from 'src/common/utils'
import { DEFAULT_QR_LINK } from 'src/constants'

interface IFormTitleProps {
  imageComponent: ReactElement
  breadcrumbs?: IBreadcrumb[]
  setFieldValue?: (field: string, value: any) => void
  title: string | null
  menuItems: IMenuItem[]
  handleChooseMenuItem: (currentItem: IMenuItem) => void
  afterTitleNode?: ReactNode
  afterBreadcrumbsNode?: ReactNode
  showQRCodeButton?: boolean
  productStatus?: boolean
  statusType?: 'account' | 'product'
  showStatus?: boolean
  isAddMode?: boolean
  simpleId?: string
  locale?: string
}

const FormTitle = function ({
  breadcrumbs,
  title,
  menuItems,
  handleChooseMenuItem,
  imageComponent,
  afterTitleNode,
  afterBreadcrumbsNode,
  showQRCodeButton = false,
  showStatus,
  productStatus,
  statusType = 'product',
  isAddMode,
  simpleId,
  locale,
}: IFormTitleProps) {
  const [t] = useTranslation()
  const theme = useTheme()
  const classes = useStyles({})
  const navigate = useNavigate()

  const hadnleBreacrumbsClick = (path: string) => () => navigate(path)

  const handlOpen = (link: string) => {
    window.open(link + '?preview=true', '_blank')
  }

  const qrLink = simpleId
    ? createProductQRLink({ productId: simpleId, lng: locale || DEFAULT_LANGUAGE_CODE })
    : DEFAULT_QR_LINK

  return (
    <Grid className={classes.formTitleWrapper}>
      <div className={classes.breadcrumbsWrapper}>
        {breadcrumbs && (
          <Breadcrumbs separator={<ArrowIcon />}>
            {breadcrumbs.map(({ text, path }) => {
              return (
                <Button
                  onClick={path ? hadnleBreacrumbsClick(path) : undefined}
                  variant="text"
                  key={text}
                  size="small"
                  disabled={!path}
                  className={`${classes.breadcrumbText}`}
                >
                  {t(text)}
                </Button>
              )
            })}
          </Breadcrumbs>
        )}
        {afterBreadcrumbsNode}
      </div>
      <Grid className={classes.titleWrapper}>
        <Grid className={classes.titleWrapperLeft}>
          <Grid className={classes.imageWrapper}>{imageComponent}</Grid>
          <Typography className={classes.titleText}>{title}&nbsp;</Typography>
          {afterTitleNode}

          {showQRCodeButton && (
            <ScrollToButton
              icon={
                <QRCodeSVG
                  style={{
                    width: '80px',
                    height: '80px',
                  }}
                  value={qrLink}
                  level="L"
                  fgColor={theme.palette.custom.black}
                  bgColor={theme.palette.custom.white}
                />
              }
              referenceId="product-qr-code-section"
            />
          )}
        </Grid>
        {showStatus && !isAddMode && (
          <Box sx={{ marginLeft: '16px' }}>
            <ColoredChip
              color={productStatus ? 'green' : '#E57373'}
              text={productStatus ? t('product-public-lable.v') : t('product-not-public-lable.v')}
              icon={
                productStatus ? null : statusType === 'product' ? (
                  <IconButton
                    sx={{
                      padding: '2px',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      handlOpen(
                        createProductQRLink({
                          productId: simpleId || '',
                          lng: locale || DEFAULT_LANGUAGE_CODE,
                        }),
                      )
                    }}
                  >
                    <ExternalLink size={24} stroke={'#E57373'} />
                  </IconButton>
                ) : null
              }
            />
          </Box>
        )}
      </Grid>
      <Grid className={classes.menuItemWrapper}>
        <ItemsMenu items={menuItems} handleChooseItem={handleChooseMenuItem} />
      </Grid>
    </Grid>
  )
}
export { FormTitle, ImageInput }
